<template>
  <div
    class="w-full h-full flex flex-col relative animate-fade-in"
    :class="theme.text"
  >
    <div class="shrink-0 h-12 flex items-center justify-between px-4 z-10">
      <button @click="$emit('back')">
        <icon-base
          name="ChevronLeft"
          class="w-6 h-6 opacity-60 hover:opacity-100"
        ></icon-base>
      </button>
      <span class="font-bold">{{ t.member_title }}</span>
      <div class="w-6"></div>
    </div>
    <div class="flex-1 overflow-y-auto px-4 pb-4">
      <div class="text-center mb-6 mt-1">
        <div
          class="inline-block p-2 rounded-xl bg-gradient-to-tr from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30 mb-2"
        >
          <icon-base name="Crown" class="w-6 h-6 text-white"></icon-base>
        </div>
        <h2 class="font-bold mb-1" :class="isWatch ? 'text-lg' : 'text-2xl'">
          {{ t.unlock_title }}
        </h2>
        <p v-if="!isWatch" class="opacity-60 text-xs">{{ t.unlock_desc }}</p>
      </div>
      <div class="flex flex-col gap-3 mb-6">
        <button
          @click="plan = 'monthly'"
          class="w-full p-3 rounded-xl border-2 transition-all relative flex items-center justify-between"
          :class="
            plan === 'monthly'
              ? 'border-amber-500 bg-amber-500/5'
              : theme.isDark
              ? 'border-white/10'
              : 'border-black/5'
          "
        >
          <div class="flex items-center gap-3">
            <div
              class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
              :class="
                plan === 'monthly' ? 'border-amber-500' : 'border-gray-400'
              "
            >
              <div
                v-if="plan === 'monthly'"
                class="w-2 h-2 rounded-full bg-amber-500"
              ></div>
            </div>
            <span class="font-bold text-sm">{{ t.plan_month }}</span>
          </div>
          <div class="font-bold text-sm">
            ¥12<span class="text-[10px] font-normal opacity-50">/M</span>
          </div>
        </button>
        <button
          @click="plan = 'yearly'"
          class="w-full p-3 rounded-xl border-2 transition-all relative flex items-center justify-between"
          :class="
            plan === 'yearly'
              ? 'border-amber-500 bg-amber-500/5'
              : theme.isDark
              ? 'border-white/10'
              : 'border-black/5'
          "
        >
          <div
            class="absolute -top-2 right-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm"
          >
            {{ t.recommend }}
          </div>
          <div class="flex items-center gap-3">
            <div
              class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
              :class="
                plan === 'yearly' ? 'border-amber-500' : 'border-gray-400'
              "
            >
              <div
                v-if="plan === 'yearly'"
                class="w-2 h-2 rounded-full bg-amber-500"
              ></div>
            </div>
            <div class="text-left">
              <div class="font-bold text-sm">{{ t.plan_year }}</div>
              <div class="text-[9px] text-amber-500 font-bold">
                {{ t.save_pct }}
              </div>
            </div>
          </div>
          <div class="text-right">
            <div class="font-bold text-sm">
              ¥98<span class="text-[10px] font-normal opacity-50">/Y</span>
            </div>
          </div>
        </button>
        <button
          @click="plan = 'lifetime'"
          class="w-full p-3 rounded-xl border-2 transition-all relative flex items-center justify-between"
          :class="
            plan === 'lifetime'
              ? 'border-amber-500 bg-amber-500/5'
              : theme.isDark
              ? 'border-white/10'
              : 'border-black/5'
          "
        >
          <div class="flex items-center gap-3">
            <div
              class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
              :class="
                plan === 'lifetime' ? 'border-amber-500' : 'border-gray-400'
              "
            >
              <div
                v-if="plan === 'lifetime'"
                class="w-2 h-2 rounded-full bg-amber-500"
              ></div>
            </div>
            <div class="text-left">
              <div class="font-bold text-sm">{{ t.plan_lifetime }}</div>
              <div class="text-[9px] opacity-60">{{ t.save_lifetime }}</div>
            </div>
          </div>
          <div class="text-lg font-bold">{{ t.lifetime_price }}</div>
        </button>
      </div>
      <div
        class="rounded-xl p-4 mb-4"
        :class="theme.isDark ? 'bg-white/5' : 'bg-black/5'"
      >
        <div
          class="text-[10px] font-bold opacity-40 uppercase tracking-wider mb-3"
        >
          {{ t.privilege }}
        </div>
        <div class="space-y-3">
          <div v-for="(item, i) in t.privileges" :key="i" class="flex gap-3">
            <div class="mt-0.5">
              <icon-base
                name="Check"
                class="w-3 h-3 text-amber-500"
              ></icon-base>
            </div>
            <div>
              <div class="text-xs font-bold">{{ item.t }}</div>
              <div v-if="!isWatch" class="text-[10px] opacity-50">
                {{ item.d }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="p-4 border-t safe-bottom"
      :class="
        theme.isDark
          ? 'border-white/10 bg-[#0f1115]'
          : 'border-black/5 bg-white'
      "
    >
      <button
        @click="handlePay"
        :disabled="loading"
        class="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-600 text-white font-bold text-base shadow-lg shadow-orange-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
      >
        <icon-base
          v-if="loading"
          name="Refresh"
          class="w-4 h-4 animate-spin"
        ></icon-base>
        <span v-else>{{ t.pay_btn }} {{ price }}</span>
      </button>
      <div v-if="!isWatch" class="text-[9px] text-center opacity-40 mt-2">
        {{ t.pay_agreement }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["theme", "t", "device"],
  data() {
    return { plan: "yearly", loading: false };
  },
  computed: {
    isWatch() {
      return this.device === "WATCH";
    },
    price() {
      if (this.plan === "lifetime") return this.t.lifetime_price;
      return this.plan === "yearly" ? "¥98" : "¥12";
    },
  },
  methods: {
    handlePay() {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
        this.$emit("success");
      }, 2000);
    },
  },
};
</script>
